import React, { useEffect, useState } from 'react';
import { View, Text } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import Animated, {
  useSharedValue,
  useAnimatedStyle,
  withTiming,
  Easing,
} from 'react-native-reanimated';
import { ShieldCheck, Users, TrendingUp } from 'lucide-react-native';
import { useThemeColors } from '@/lib/useThemeColors';
import { formatCurrency } from '@/lib/utils/format';
import type { CommitmentSummary } from '@/lib/state/inverio-store';

interface CommitmentProgressProps {
  commitmentSummary: CommitmentSummary;
  compact?: boolean;
}

export function CommitmentProgress({
  commitmentSummary,
  compact = false,
}: CommitmentProgressProps) {
  const colors = useThemeColors();
  const {
    fundingTarget,
    totalVerifiedCommitted,
    totalUnverifiedCommitted,
    totalCommitted,
    percentageFilled,
    verifiedInvestorCount,
    unverifiedInvestorCount,
    softCommitCount,
    hardCommitCount,
  } = commitmentSummary;

  const verifiedPercent = fundingTarget > 0
    ? Math.min((totalVerifiedCommitted / fundingTarget) * 100, 100)
    : 0;
  const unverifiedPercent = fundingTarget > 0
    ? Math.min((totalUnverifiedCommitted / fundingTarget) * 100, 100 - verifiedPercent)
    : 0;

  // Measure track width via onLayout, then animate to pixel targets.
  // Bug context: useAnimatedStyle with percentage strings (`${v}%`) silently
  // breaks on native — the worklet can't interpolate template literals.
  // Fix: animate numeric pixel values instead. This preserves the smooth
  // withTiming growth animation while working on all platforms.
  const [trackWidth, setTrackWidth] = useState(0);

  const verifiedWidthSV = useSharedValue(0);
  const unverifiedWidthSV = useSharedValue(0);

  useEffect(() => {
    if (trackWidth <= 0) return;
    const targetVerified = (verifiedPercent / 100) * trackWidth;
    const targetUnverified = (unverifiedPercent / 100) * trackWidth;

    verifiedWidthSV.value = withTiming(targetVerified, {
      duration: 800,
      easing: Easing.out(Easing.cubic),
    });
    unverifiedWidthSV.value = withTiming(targetUnverified, {
      duration: 800,
      easing: Easing.out(Easing.cubic),
    });
  }, [verifiedPercent, unverifiedPercent, trackWidth]);

  const verifiedStyle = useAnimatedStyle(() => ({
    width: verifiedWidthSV.value,
  }));

  const unverifiedStyle = useAnimatedStyle(() => ({
    width: unverifiedWidthSV.value,
  }));

  const remaining = Math.max(0, fundingTarget - totalCommitted);
  const totalInvestors = verifiedInvestorCount + unverifiedInvestorCount;

  if (compact) {
    return (
      <View>
        {/* Compact progress bar */}
        <View className="flex-row items-center justify-between mb-1.5">
          <Text
            className="text-xs font-semibold"
            style={{ color: colors.text.primary }}
          >
            {formatCurrency(totalCommitted)} / {formatCurrency(fundingTarget)}
          </Text>
          <Text
            className="text-xs font-medium"
            style={{ color: colors.text.tertiary }}
          >
            {Math.round(percentageFilled)}%
          </Text>
        </View>
        <View
          className="h-2 rounded-full overflow-hidden flex-row"
          style={{ backgroundColor: colors.border.light }}
          onLayout={(e) => setTrackWidth(e.nativeEvent.layout.width)}
        >
          <Animated.View style={[{ borderRadius: 999 }, verifiedStyle]}>
            <LinearGradient
              colors={[colors.primary[500], colors.primary[600]]}
              start={{ x: 0, y: 0 }}
              end={{ x: 1, y: 0 }}
              style={{ height: 8, borderRadius: 999 }}
            />
          </Animated.View>
          <Animated.View style={[{ borderRadius: 999 }, unverifiedStyle]}>
            <LinearGradient
              colors={[`${colors.primary[400]}80`, `${colors.primary[500]}60`]}
              start={{ x: 0, y: 0 }}
              end={{ x: 1, y: 0 }}
              style={{ height: 8, borderRadius: 999 }}
            />
          </Animated.View>
        </View>
        <View className="flex-row items-center mt-1.5 gap-3">
          <View className="flex-row items-center gap-1">
            <View className="w-2 h-2 rounded-full" style={{ backgroundColor: colors.primary[500] }} />
            <Text className="text-[10px]" style={{ color: colors.text.tertiary }}>
              Verified
            </Text>
          </View>
          <View className="flex-row items-center gap-1">
            <View className="w-2 h-2 rounded-full" style={{ backgroundColor: `${colors.primary[400]}80` }} />
            <Text className="text-[10px]" style={{ color: colors.text.tertiary }}>
              Unverified
            </Text>
          </View>
          {totalInvestors > 0 && (
            <Text className="text-[10px] ml-auto" style={{ color: colors.text.tertiary }}>
              {totalInvestors} investor{totalInvestors !== 1 ? 's' : ''}
            </Text>
          )}
        </View>
      </View>
    );
  }

  return (
    <View>
      {/* Header row */}
      <View className="flex-row items-center justify-between mb-3">
        <View className="flex-row items-center gap-2">
          <TrendingUp size={16} color={colors.primary[500]} />
          <Text
            className="text-sm font-semibold"
            style={{ color: colors.text.primary }}
          >
            {formatCurrency(totalCommitted)}
          </Text>
          <Text
            className="text-sm"
            style={{ color: colors.text.tertiary }}
          >
            of {formatCurrency(fundingTarget)}
          </Text>
        </View>
        <View
          className="px-2.5 py-1 rounded-full"
          style={{ backgroundColor: `${colors.primary[500]}18` }}
        >
          <Text
            className="text-xs font-bold"
            style={{ color: colors.primary[500] }}
          >
            {Math.round(percentageFilled)}%
          </Text>
        </View>
      </View>

      {/* Progress bar */}
      <View
        className="h-3 rounded-full overflow-hidden flex-row"
        style={{ backgroundColor: colors.border.light }}
        onLayout={(e) => setTrackWidth(e.nativeEvent.layout.width)}
      >
        <Animated.View style={[{ borderRadius: 999 }, verifiedStyle]}>
          <LinearGradient
            colors={[colors.primary[500], colors.primary[700]]}
            start={{ x: 0, y: 0 }}
            end={{ x: 1, y: 0 }}
            style={{ height: 12, borderRadius: 999 }}
          />
        </Animated.View>
        <Animated.View style={[{ borderRadius: 999 }, unverifiedStyle]}>
          <LinearGradient
            colors={[`${colors.primary[400]}80`, `${colors.primary[500]}60`]}
            start={{ x: 0, y: 0 }}
            end={{ x: 1, y: 0 }}
            style={{ height: 12, borderRadius: 999 }}
          />
        </Animated.View>
      </View>

      {/* Legend */}
      <View className="flex-row items-center mt-2 gap-4">
        <View className="flex-row items-center gap-1.5">
          <View className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: colors.primary[500] }} />
          <Text className="text-xs" style={{ color: colors.text.secondary }}>
            Verified
          </Text>
        </View>
        <View className="flex-row items-center gap-1.5">
          <View className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: `${colors.primary[400]}80` }} />
          <Text className="text-xs" style={{ color: colors.text.secondary }}>
            Unverified
          </Text>
        </View>
      </View>

      {/* Mini stat cards */}
      <View className="flex-row mt-4 gap-2">
        {/* Verified card */}
        <View
          className="flex-1 p-3 rounded-xl"
          style={{
            backgroundColor: `${colors.primary[500]}10`,
            borderWidth: 1,
            borderColor: `${colors.primary[500]}20`,
          }}
        >
          <View className="flex-row items-center gap-1.5 mb-1">
            <ShieldCheck size={12} color={colors.primary[500]} />
            <Text className="text-[10px] font-medium uppercase tracking-wider" style={{ color: colors.primary[500] }}>
              Verified
            </Text>
          </View>
          <Text className="text-base font-bold" style={{ color: colors.text.primary }}>
            {formatCurrency(totalVerifiedCommitted)}
          </Text>
          <Text className="text-[10px] mt-0.5" style={{ color: colors.text.tertiary }}>
            {verifiedInvestorCount} investor{verifiedInvestorCount !== 1 ? 's' : ''}
          </Text>
        </View>

        {/* Unverified card */}
        <View
          className="flex-1 p-3 rounded-xl"
          style={{
            backgroundColor: colors.surface.secondary,
            borderWidth: 1,
            borderColor: colors.border.light,
          }}
        >
          <View className="flex-row items-center gap-1.5 mb-1">
            <Users size={12} color={colors.text.tertiary} />
            <Text className="text-[10px] font-medium uppercase tracking-wider" style={{ color: colors.text.tertiary }}>
              Unverified
            </Text>
          </View>
          <Text className="text-base font-bold" style={{ color: colors.text.primary }}>
            {formatCurrency(totalUnverifiedCommitted)}
          </Text>
          <Text className="text-[10px] mt-0.5" style={{ color: colors.text.tertiary }}>
            {unverifiedInvestorCount} source{unverifiedInvestorCount !== 1 ? 's' : ''}
          </Text>
        </View>

        {/* Remaining card */}
        <View
          className="flex-1 p-3 rounded-xl"
          style={{
            backgroundColor: colors.surface.secondary,
            borderWidth: 1,
            borderColor: colors.border.light,
          }}
        >
          <Text className="text-[10px] font-medium uppercase tracking-wider mb-1" style={{ color: colors.text.tertiary }}>
            Remaining
          </Text>
          <Text className="text-base font-bold" style={{ color: colors.text.primary }}>
            {formatCurrency(remaining)}
          </Text>
          <Text className="text-[10px] mt-0.5" style={{ color: colors.text.tertiary }}>
            {hardCommitCount} hard / {softCommitCount} soft
          </Text>
        </View>
      </View>
    </View>
  );
}
